import { createMainAppMicroserviceInstance } from '@servicetitan/cp-api';
import {
    ServiceAddress,
    createServiceAddress,
    TimezoneDate,
    PortalStore,
    portalStoreToken,
    getTimezoneCode,
} from '@servicetitan/cp-ui';
import { inject, injectable } from '@servicetitan/react-ioc';
import { CustomerPortalCustomers } from './portal-customers.api';
export { DashboardCustomerInvoicesModel, DashboardInvoiceModel } from './portal-invoices.api';
import {
    AppointmentModel as AppointmentModelBase,
    AppointmentStatus,
    MembershipStatus,
    Dashboard,
    DashboardMembershipSummaryModel,
    EstimateContainerWebModel,
} from './portal-dashboard.api';
import { PortalInvoices, DashboardCustomerInvoicesModel } from './portal-invoices.api';

export interface CustomerAddresses {
    id: number;
    name: string;
    addresses: ServiceAddress[];
}

export interface AppointmentModel
    extends Omit<
        AppointmentModelBase,
        'startDate' | 'endDate' | 'jobId' | 'proposalId' | 'isConfirmed'
    > {
    startDate: TimezoneDate;
    endDate: TimezoneDate;
}

export interface VisitModel
    extends Omit<
        DashboardMembershipSummaryModel,
        'from' | 'to' | 'nextBillingDate' | 'customerId'
    > {
    from?: TimezoneDate;
    to?: TimezoneDate;
    nextBillingDate?: TimezoneDate;
}

export interface DashboardInfo {
    customerInvoices: DashboardCustomerInvoicesModel[];
    appointments: AppointmentModel[];
    proposals: EstimateContainerWebModel[];
    visits: VisitModel[];
}

// somehow direct export from api file doesn't work
export { AppointmentStatus, MembershipStatus };

export const processAppointments = (
    items: AppointmentModelBase[],
    tz: string
): AppointmentModel[] =>
    items.map(item => ({
        ...item,
        startDate: TimezoneDate.fromDateUTC(tz, item.startDate),
        endDate: TimezoneDate.fromDateUTC(tz, item.endDate),
    }));

export const processVisits = (items: DashboardMembershipSummaryModel[]): VisitModel[] =>
    items.map(item => ({
        ...item,
        from: item.from ? TimezoneDate.dateFromDateLocal(item.from) : undefined,
        to: item.to ? TimezoneDate.dateFromDateLocal(item.to) : undefined,
        nextBillingDate: item.nextBillingDate
            ? TimezoneDate.dateFromDateLocal(item.nextBillingDate)
            : undefined,
    }));

@injectable()
export class DashboardApi {
    invoice: PortalInvoices;
    private customers: CustomerPortalCustomers;
    private dashboard: Dashboard;

    constructor(@inject(portalStoreToken) private portalStore: PortalStore) {
        this.dashboard = new Dashboard(
            createMainAppMicroserviceInstance('/v1/customerportal2/dashboard', '/api/cp/dashboard')
        );
        // TODO: make it use V2 endpoint without error
        this.customers = new CustomerPortalCustomers(
            createMainAppMicroserviceInstance('/v1/customerportal2/customers', '/api/cp/customers')
        );

        this.invoice = new PortalInvoices(
            createMainAppMicroserviceInstance('/v1/customerportal2/invoices', '/api/cp/invoices')
        );
    }

    getCustomerAddresses(): Promise<CustomerAddresses[]> {
        return this.customers.getServiceAddresses().then(({ data }) =>
            data.map(customer => ({
                id: customer.id,
                name: customer.name,
                addresses: customer.locations.map(location =>
                    createServiceAddress(location.id, location.name, location.address ?? {})
                ),
            }))
        );
    }

    getDashboardData(locationIds: number[]): Promise<DashboardInfo> {
        return this.dashboard.dashboardData(locationIds, false).then(({ data }) => ({
            customerInvoices: data.customersInvoices,
            appointments: processAppointments(
                data.upcomingAppointments,
                this.portalStore.settings?.timezoneCode ?? getTimezoneCode()
            ),
            proposals: data.latestEstimateContainers,
            visits: processVisits(data.visitsDueMemberships ?? []),
        }));
    }
}
